import { StatusMessage } from "../../src-logic/status/status-message";
import {getState} from "../../src-logic/state/state";
import {History} from "../../src-logic/history/history-overlay";
import {submitFeedback} from "../../src-worker/s2k-feedback";
import {MetricsNames} from "../../src-logic/metrics/metrics";
import {NETWORK_STATUS_CODE} from "../../src-common/constants";
import {addUnloadListener, preventFromUnload, removeUnloadListener} from "../../src-common/utils/s2k-before-unload";
import {getUnzippedEpub, updateEpubMetadata} from "../../src-common/utils/s2k-epub-helper";

/**
 * The view controller of preview page.
 */
class Controller {
    // ################################ Initialization ################################
    /**
     * @param {import("./view").View} view 
     * @param {import("./extensionService").ExtensionService} extensionService
     */
    constructor(view, extensionService) {
        // Dependencies
        this.view = view;
        this.extensionService = extensionService;
        this.statusMessage = new StatusMessage("alert");

        // Progress state
        this.inSending = false;

        this.isSelection = false;

        /**
         * The current document, constructed via
         * @type {string | undefined}
         */
        this.currentDocumentToken = undefined;
        this.contentModel = undefined;
        this.unzippedEpub = undefined;

        // Register View event handlers
        this.view.registerEvent("back", this.handleBack.bind(this));
        this.view.registerEvent("send", this.handleSend.bind(this));
        this.view.registerEvent("feedback", this.handleFeedback.bind(this));
        this.statusMessage.registerEvent("cancelProgress", this.handleCancel.bind(this));
        this.statusMessage.registerEvent("submitFeedback", this.handleFeedbackSubmit.bind(this));
        chrome.runtime.onMessage.addListener(this.handleMessages.bind(this));
    }

    /**
     * Load UI staffs of View, and Set document to the content of preview view
     */
    async load() {
        // Prevent from unload page
        addUnloadListener(preventFromUnload);
        // Get params
        this.getParamsFromUrl();
        // Init UI
        this.view.init(this.isSelection);
        // Load preview content
        if (this.currentDocumentToken) {
            const content = await this.extensionService.fetchDocument(this.currentDocumentToken);
            this.contentModel = content;
            // Get epub
            if (content.epubString) {
                this.unzippedEpub = await getUnzippedEpub(content.epubString);
            }
            await this.view.setContent(content, this.unzippedEpub);
        }
    }

    /**
     * Show history page in preview page
     */
    showHistory() {
        const state = getState();

        if (!state.history) {
            state.history = new History();
        }

        state.history.inject();
    }

    // ################################ Events handler ################################
    /**
     * Handle the send action from preview page
     * @param {string} title The title of document
     * @param {string} author The author of document
     */
    async handleSend(title, author) {
        // Report domain
        chrome.runtime.sendMessage({
            action: "report-domain",
            data: {
                operation: "pre-s",
                domain: this.contentModel.source
            }
        });
        // Check if sending in progress
        if (this.inSending) {
            return;
        }

        // Update title and author based on user input
        this.updateInput(title, author, function () {
            // Set progress state
            this.inSending = true;

            chrome.runtime.sendMessage({
                action: "set-cancel-sending",
                data: {
                    cancel: false
                }
            });

            // Show send in progress alert
            this.statusMessage.showAlert("progress", "sendingToKindle");

            this.extensionService.sendDocument(this.currentDocumentToken, function (res) {
                if (res.statusCode === NETWORK_STATUS_CODE.NO_NETWORK) {
                    // Show network alert
                    this.statusMessage.showAlert("result", "networkError");
                } else if (res.success && res.statusCode !== NETWORK_STATUS_CODE.CANCEL) {
                    // Show success alert if send failed
                    this.statusMessage.showAlert("result","sending");
                } else if (res.statusCode !== NETWORK_STATUS_CODE.CANCEL) {
                    // Show error alert if send failed
                    this.statusMessage.showAlert("result", "generalError");
                }

                // Reset progress state after sending
                this.inSending = false;
                chrome.runtime.sendMessage({
                    action: "set-cancel-sending",
                    data: {
                        cancel: false
                    }
                });
            }.bind(this));
        }.bind(this)).then();
    }

    /**
     * Handle the back action from preview page
     */
    handleBack() {
        // Stop monitoring unload action
        removeUnloadListener(preventFromUnload);
        history.back();
    }

    /**
     * Update title and author from user input
     * @param {string} title The title of document
     * @param {string} author The author of document
     * @param {function} callback Complete callback
     */
    async updateInput(title, author, callback) {
        //Update epub file based on new title and new author
        let epubString = await updateEpubMetadata(this.contentModel.epubString, title, author);
        this.contentModel.epubString = epubString;
        this.extensionService.updateMetadata(this.currentDocumentToken, {title, author, epubString}, function (newToken) {
            this.currentDocumentToken = newToken || this.currentDocumentToken;
            if (callback ) {
                callback();
            }
        }.bind(this));
        // Record metrics if the tile/author is edited
        if (this.contentModel.title !== title) {
            chrome.runtime.sendMessage({
                action: "metrics-add-counter",
                data: {
                    metricsName: MetricsNames["preview-send-click-title"]
                }
            });
        }
        if (this.contentModel.author !== author) {
            chrome.runtime.sendMessage({
                action: "metrics-add-counter",
                data: {
                    metricsName: MetricsNames["preview-send-click-author"]
                }
            });
        }

        this.contentModel.title = title;
        this.contentModel.author = author;
    }

    /**
     * Handle a content script message.
     * @param {{action: string, data: {}}}message       Message
     * @param sender        Sender Object
     * @param {function} callback  Callback
     */
    handleMessages(message, sender, callback) {
        // Handle show history in preview page
        if (message.action === "show-history-preview") {
            this.showHistory();
            callback();
        }
        return true;
    }

    /**
     * Handle the feedback action from preview page
     */
    handleFeedback() {
        this.statusMessage.showAlert("feedback", "input");
    }

    /**
     * Handle the feedback submit action from preview page
     * @param {{text: string, rating: number}} userInput
     */
    handleFeedbackSubmit(userInput) {
        if (userInput.rating <= 2) {
            // Report low rating
            chrome.runtime.sendMessage({
                action: "report-low-rating",
                data: {
                    rating: userInput.rating,
                    url: this.contentModel.url
                }
            });
        }
        // Record metrics
        let starMetricsName = MetricsNames["preview-feedback-submit-click"] + userInput.rating;
        chrome.runtime.sendMessage({
            action: "metrics-add-counter",
            data: {
                metricsName: starMetricsName
            }
        });
        let textMetricsName = userInput.text.trim().length === 0 ? MetricsNames["preview-feedback-submit-empty"] : MetricsNames["preview-feedback-submit-input"];
        chrome.runtime.sendMessage({
            action: "metrics-add-counter",
            data: {
                metricsName: textMetricsName
            }
        });
        // Submit feedback
        submitFeedback(userInput.text, userInput.rating, "Preview", this.contentModel.url);
        // Show feedback finish alert
        this.statusMessage.showAlert("feedback", "finish");
    }

    /**
     * Handle cancel sending event
     */
    handleCancel() {
        this.inSending = false;
        chrome.runtime.sendMessage({
            action: "set-cancel-sending",
            data: {
                cancel: true
            }
        });
    }

    /**
     * Retrieve params from url
     * 1. document token (Since we add the token to the url while generate it)
     * 2. is from selection or not
     */
    getParamsFromUrl() {
        const urlParams = new URLSearchParams(document.location.search);
        this.currentDocumentToken =  urlParams.get("article");
        this.isSelection = urlParams.get("isSelection") === "true";
    }
}

export { Controller };
